import { useRef, useState } from 'react';
import { Formik } from 'formik';
import { AdminCustomerFormProps, AdminCustomerFormType } from './AdminCustomerForm.types';
import { AccountType } from '../../../../../API/Enums';
import { getROAppointment, IAppointment } from '../../../../../API/GoMoto';
import { GoMotoComponent, GoMotoFunction } from '../../../../../API/GoMoto.types';
import { useEnsureAppointment, useAppointmentUpdateAndPersist } from '../../../../../context/Appointment/appointment';
import { useKioskState } from '../../../../../context/Kiosk/kiosk';
import { useLocalization } from '../../../../../context/Localization/localization';
import { LocalizationType } from '../../../../../context/Localization/localization.types';
import { CTAButton } from '../../../../../controls/ButtonBase';
import { GMForm, GMFormWrapper } from '../../../../../controls/Form';
import { KeyboardLayouts } from '../../../../../controls/SimpleKeyboard/Layout';
import { Spinner } from '../../../../../controls/Spinner/Spinner';
import { GMFormRoNumberField, GMFormTextField } from '../../../../../controls/TextField';
import { nameof } from '../../../../../helpers/nameof';
import { KeyManagementHeader } from '../../../../../controls/KeyManagementHeader/KeyManagementHeader';
import { GMDialogYesNo } from '../../../../../controls/Dialog';
import { getFullDisplayName } from '../../../../../helpers/Appointment';

/**
 * The first screen when an advisor creates a new admin pickup appointment or begins
 * storing a key for an existing appointment.
 *
 * With the RO Number field, this component will autofill an appointment if the
 * RO Number matches the most recent appointment that is not completed and is scheduled.
 *
 * @param onProceed - {@link AdminCustomerFormProps.onProceed onProceed} description.
 * @param isAdminCreate - {@link AdminCustomerFormProps.isAdminCreate isAdminCreate} description.
 */
export const AdminCustomerForm: GoMotoComponent<AdminCustomerFormProps> = ({
    onProceed,
    isAdminCreate,
}) => {
    const Localization: LocalizationType = useLocalization();
    const { dealer: { key: dealerKey, primaryLanguage, keyTrakSystemId } } = useKioskState();
    const { appointment, isLoading } = useEnsureAppointment(true);
    const setAppointment = useAppointmentUpdateAndPersist(true);

    const {
        firstName = '',
        lastName = '',
        businessName = '',
        roNumber = '',
        accountType = AccountType.individual,
        email = '',
        phone = '',
        contactPreference = undefined,
        locale = primaryLanguage,
    } = appointment || {};

    const initialValues: AdminCustomerFormType = {
        firstName: accountType === AccountType.business ? '' : firstName ?? '',
        lastName: accountType === AccountType.business ? '' : lastName ?? '',
        businessName: businessName ?? '',
        roNumber,
        accountType,
        email,
        phone,
        contactPreference,
        locale,
    };
    const roNumberRef = useRef<string>('');
    const [loadingRoAutofill, setLoadingRoAutofill] = useState<boolean>(false);
    const [showRoNumberWarningModal, setShowRoNumberWarningModal] = useState<boolean>(false);

    const shouldCancelRef = useRef<boolean>(false);
    const resolveModalPromise = useRef<GoMotoFunction<[void | PromiseLike<void>], void> | null>(null);
    const prevAppointmentRef = useRef<AdminCustomerFormType | null>(null);
    const newAppointmentRef = useRef<IAppointment | null>(null);

    const waitForModalToClose: GoMotoFunction<void, Promise<void>> = () => {
        return new Promise<void>((resolve) => {
            resolveModalPromise.current = resolve;
        });
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={async (values, { setSubmitting }) => {
                prevAppointmentRef.current = {
                    ...values,
                    firstName: values.firstName ?? '',
                    lastName: values.lastName ?? '',
                    businessName: values.businessName ?? '',
                };
                newAppointmentRef.current = await getROAppointment(values.roNumber ?? '', dealerKey);

                if (newAppointmentRef.current !== null) {
                    newAppointmentRef.current = {
                        ...newAppointmentRef.current,
                        businessName: newAppointmentRef.current?.businessName ?? '',
                        firstName: newAppointmentRef.current?.firstName ?? '',
                        lastName: newAppointmentRef.current?.lastName ?? '',
                    };
                }

                const hasBusinessNameChanged: boolean = newAppointmentRef.current?.businessName !== prevAppointmentRef.current.businessName;
                const hasCustomerNameChanged: boolean = newAppointmentRef.current?.firstName !== prevAppointmentRef.current.firstName
                    || newAppointmentRef.current.lastName !== prevAppointmentRef.current.lastName;
                const hasPhoneChanged: boolean = newAppointmentRef.current?.phone !== prevAppointmentRef.current.phone;
                const hasEmailChanged: boolean = newAppointmentRef.current?.email !== prevAppointmentRef.current.email;
                const hasContactPreferenceChanged: boolean = newAppointmentRef.current?.contactPreference !== prevAppointmentRef.current.contactPreference;

                if (newAppointmentRef.current !== null && (hasBusinessNameChanged || hasCustomerNameChanged)) {
                    setShowRoNumberWarningModal(true);
                    await waitForModalToClose();
                }

                if (shouldCancelRef.current === true) {
                    shouldCancelRef.current = false;
                    setSubmitting(false);
                }
                else {
                    if (isAdminCreate) {
                        await setAppointment({
                            ...appointment!,
                            ...values,
                            dealerKey,
                            adminCreated: true,
                            status: 'not scheduled',
                        }, true, false, false, true, undefined, false, true, false, false, false, true);
                    }
                    else {
                        let valueOverrides = {};

                        if (newAppointmentRef.current && (hasCustomerNameChanged || hasBusinessNameChanged || hasPhoneChanged || hasEmailChanged || hasContactPreferenceChanged)) {
                            if (hasBusinessNameChanged) {
                                newAppointmentRef.current = {
                                    ...newAppointmentRef.current,
                                    businessName: prevAppointmentRef.current.businessName,
                                    pickupName: newAppointmentRef.current.businessName,
                                };

                                valueOverrides = {
                                    ...valueOverrides,
                                    businessName: prevAppointmentRef.current.businessName,
                                    pickupName: newAppointmentRef.current.businessName,
                                };
                            }
                            if (hasCustomerNameChanged) {
                                newAppointmentRef.current = {
                                    ...newAppointmentRef.current,
                                    firstName: prevAppointmentRef.current.firstName,
                                    lastName: prevAppointmentRef.current.lastName,
                                    pickupName: `${newAppointmentRef.current.firstName} ${newAppointmentRef.current.lastName}`,
                                };

                                valueOverrides = {
                                    ...valueOverrides,
                                    firstName: prevAppointmentRef.current.firstName,
                                    lastName: prevAppointmentRef.current.lastName,
                                    pickupName: `${newAppointmentRef.current.firstName} ${newAppointmentRef.current.lastName}`,
                                };
                            }
                            if (hasPhoneChanged) {
                                newAppointmentRef.current = {
                                    ...newAppointmentRef.current,
                                    phone: prevAppointmentRef.current.phone,
                                    pickupPhone: newAppointmentRef.current.phone,
                                };

                                valueOverrides = {
                                    ...valueOverrides,
                                    phone: prevAppointmentRef.current.phone,
                                    pickupPhone: newAppointmentRef.current.phone,
                                };
                            }
                            if (hasEmailChanged) {
                                newAppointmentRef.current = {
                                    ...newAppointmentRef.current,
                                    email: prevAppointmentRef.current.email,
                                    pickupEmail: newAppointmentRef.current.email,
                                };

                                valueOverrides = {
                                    ...valueOverrides,
                                    email: prevAppointmentRef.current.email,
                                    pickupEmail: newAppointmentRef.current.email,
                                };
                            }
                            if (hasContactPreferenceChanged) {
                                newAppointmentRef.current = {
                                    ...newAppointmentRef.current,
                                    contactPreference: prevAppointmentRef.current.contactPreference,
                                    pickupContactPreference: newAppointmentRef.current.contactPreference,
                                };

                                valueOverrides = {
                                    ...valueOverrides,
                                    contactPreference: prevAppointmentRef.current.contactPreference,
                                    pickupContactPreference: newAppointmentRef.current.contactPreference,
                                };
                            }
                        }

                        await setAppointment({
                            ...appointment!,
                            ...values,
                            ...valueOverrides,
                        }, true, false, false, true, undefined, false, true);
                    }

                    onProceed();
                }
            }}
        >
            {({ values, setFieldValue }) => {
                const getAppointmentByRoNumber = (value: string, dealerKey: string, setFieldValue: (field: string, value: any) => void) => {
                    roNumberRef.current = value;
                    if (!loadingRoAutofill) {
                        setLoadingRoAutofill(true);
                        getROAppointment(value, dealerKey)
                            .then((appointment: IAppointment | null) => {
                                if (appointment) {
                                    if (appointment.accountType && appointment.accountType === 'business') {
                                        setFieldValue(nameof<AdminCustomerFormType>('firstName'), '');
                                        setFieldValue(nameof<AdminCustomerFormType>('lastName'), '');
                                        appointment.businessName && !values.businessName && setFieldValue(nameof<AdminCustomerFormType>('businessName'), appointment.businessName);
                                    }
                                    else {
                                        appointment.firstName && !values.firstName && setFieldValue(nameof<AdminCustomerFormType>('firstName'), appointment.firstName);
                                        appointment.lastName && !values.lastName && setFieldValue(nameof<AdminCustomerFormType>('lastName'), appointment.lastName);
                                        setFieldValue(nameof<AdminCustomerFormType>('businessName'), '');
                                    }
                                    appointment.accountType && setFieldValue(nameof<AdminCustomerFormType>('accountType'), appointment.accountType);
                                    appointment.phone && !values.phone && setFieldValue(nameof<AdminCustomerFormType>('phone'), appointment.phone);
                                    appointment.email && !values.email && setFieldValue(nameof<AdminCustomerFormType>('email'), appointment.email);
                                    appointment.contactPreference && contactPreference === undefined
                                        && setFieldValue(nameof<AdminCustomerFormType>('contactPreference'), appointment.contactPreference);
                                    appointment.locale && setFieldValue(nameof<AdminCustomerFormType>('locale'), appointment.locale);
                                }
                            })
                            .finally(() => {
                                setLoadingRoAutofill(false);
                            })
                            .catch(() => {
                                setLoadingRoAutofill(false);
                            });
                    }
                };

                if (isLoading) {
                    return <Spinner />;
                }
                else {
                    return (
                        <>
                            <GMForm
                                heading={
                                    <KeyManagementHeader
                                        secondaryHeading={Localization.EnterROInformationToGetStarted}
                                        leftAlignSecondaryHeading
                                        addKey
                                    />
                                }
                                submitButton={
                                    <CTAButton
                                        styleType={'primary'}
                                        type={'submit'}
                                        title={Localization.ClickToProceedToNextStep}
                                        label={Localization.Proceed}
                                        id={Localization.ClickToProceedToNextStep}
                                        ariaLabel={Localization.ClickToProceedToNextStep}
                                    />
                                }
                                topDivider
                            >
                                <GMFormWrapper finePrint requiredFieldLabel>{Localization.RequiredFields}</GMFormWrapper>
                                <GMFormRoNumberField
                                    name={nameof<typeof initialValues>('roNumber')}
                                    id={nameof<typeof initialValues>('roNumber')}
                                    title={Localization.RONumber}
                                    label={Localization.RONumber}
                                    ariaLabel={Localization.RONumber}
                                    keyboardLayout={KeyboardLayouts.AlphanumericNumpad}
                                    autoFocus
                                    onBlurCallback={() => {
                                        if (!!values.roNumber && (!values.firstName && !values.lastName && !values.businessName)) {
                                            getAppointmentByRoNumber(values.roNumber.trim(), dealerKey, setFieldValue);
                                        }
                                    }}
                                    required={!!keyTrakSystemId}
                                />
                                {(values.accountType === AccountType.business) ?
                                    <GMFormTextField
                                        name={nameof<typeof initialValues>('businessName')}
                                        id={nameof<typeof initialValues>('businessName')}
                                        title={Localization.BusinessName}
                                        label={Localization.BusinessName}
                                        ariaLabel={Localization.BusinessName}
                                        shiftAfterSpace
                                        required
                                    /> :
                                    (<>
                                        <GMFormTextField
                                            name={nameof<typeof initialValues>('firstName')}
                                            id={nameof<typeof initialValues>('firstName')}
                                            title={Localization.FirstName}
                                            label={Localization.FirstName}
                                            ariaLabel={Localization.FirstName}
                                            shiftAfterSpace
                                            required
                                        />
                                        <GMFormTextField
                                            name={nameof<typeof initialValues>('lastName')}
                                            id={nameof<typeof initialValues>('lastName')}
                                            title={Localization.LastName}
                                            label={Localization.LastName}
                                            ariaLabel={Localization.LastName}
                                            shiftAfterSpace
                                            required
                                        />
                                    </>)
                                }
                            </GMForm>
                            <GMDialogYesNo
                                yesText={Localization.Confirm}
                                onYes={() => {
                                    setShowRoNumberWarningModal(false);

                                    if (resolveModalPromise.current) {
                                        resolveModalPromise.current();
                                    }
                                }}
                                noText={Localization.GoBack}
                                onNo={() => {
                                    setShowRoNumberWarningModal(false);
                                    shouldCancelRef.current = true;

                                    if (resolveModalPromise.current) {
                                        resolveModalPromise.current();
                                    }
                                }}
                                ariaLabel={Localization.RONumber}
                                open={showRoNumberWarningModal}
                            >
                                {prevAppointmentRef.current !== null && newAppointmentRef.current !== null ? (
                                    Localization.formatString(
                                        Localization.DoubleCheckingRoNumber,
                                        prevAppointmentRef.current?.roNumber ?? '',
                                        getFullDisplayName(newAppointmentRef.current as IAppointment, false),
                                        getFullDisplayName(prevAppointmentRef.current as IAppointment, false),
                                    )
                                ) : null}
                            </GMDialogYesNo>
                        </>
                    );
                }
            }}
        </Formik>
    );
};
